import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Trophy, ArrowLeft, Mail, KeyRound, ShieldCheck } from 'lucide-react';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // request | verify | done
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await authAPI.requestPasswordReset(email.trim().toLowerCase());
      const status = data?.status;
      const message = data?.message;
      if (status === 'sent') {
        toast.success(message || 'Codice inviato via email.');
        setStep('verify');
      } else {
        toast.error(message || 'Email non trovata');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore durante la richiesta';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim() || !newPassword) return;
    if (newPassword !== newPasswordConfirm) {
      toast.error('Le password non coincidono');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password troppo corta (min 8 caratteri)');
      return;
    }
    setLoading(true);
    try {
      await authAPI.verifyPasswordReset({
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: newPassword,
      });
      setStep('done');
      toast.success('Password aggiornata! Effettua il login.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Codice non valido';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="safe-public-page flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950">
      <div className="w-full max-w-md px-4">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Torna al login
        </button>

        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-blue-600" />
              <span className="font-bold text-lg">LeagueHub</span>
            </div>
            <CardTitle className="text-2xl">
              {step === 'request' && 'Password dimenticata?'}
              {step === 'verify' && 'Inserisci il codice'}
              {step === 'done' && 'Password aggiornata'}
            </CardTitle>
            <CardDescription>
              {step === 'request' && 'Ti inviamo un codice via email per reimpostare la password.'}
              {step === 'verify' && `Abbiamo inviato un codice a 6 cifre a ${email}. Valido 15 minuti.`}
              {step === 'done' && 'Puoi accedere con la nuova password.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'request' && (
              <form onSubmit={handleRequest} className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="nome@email.it"
                      className="pl-9"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Invio in corso...' : 'Invia codice'}
                </Button>
              </form>
            )}

            {step === 'verify' && (
              <form onSubmit={handleVerify} className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="code">Codice (6 cifre)</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="code"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      className="pl-9 tracking-[0.4em] text-center font-mono text-lg"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="new-password">Nuova password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Min 8 caratteri"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="new-password-confirm">Conferma nuova password</Label>
                  <Input
                    id="new-password-confirm"
                    type="password"
                    placeholder="Ripeti la password"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    required
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setStep('request'); setCode(''); }}
                    disabled={loading}
                  >
                    Indietro
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading}>
                    {loading ? 'Verifica...' : 'Reimposta password'}
                  </Button>
                </div>
              </form>
            )}

            {step === 'done' && (
              <div className="space-y-4 text-center">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30 mx-auto">
                  <ShieldCheck className="h-8 w-8 text-emerald-600" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Tutte le sessioni precedenti sono state chiuse. Effettua di nuovo il login con la nuova password.
                </p>
                <Button className="w-full" onClick={() => navigate('/')}>
                  Vai al login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
